import '@fontsource/inter/latin-400.css';
import '@fontsource/inter/latin-500.css';
import '@fontsource/inter/latin-600.css';
import '@fontsource/space-grotesk/latin-500.css';
import '@fontsource/space-grotesk/latin-600.css';
import '@fontsource/space-grotesk/latin-700.css';
import '@fontsource/newsreader/latin-400-italic.css';
import '@fontsource/newsreader/latin-500-italic.css';
import '@/i18n';
import { Box, CircularProgress, CssBaseline, ThemeProvider } from '@mui/material';
import type { User } from '@supabase/supabase-js';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/layout/AppShell';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { isChunkLoadError, reloadOnceForStaleChunk } from '@/lib/chunkReload';
import { captureRefFromUrl } from '@/lib/affiliateRef';
import { LOCAL_MOCK_AUTH_CHANGED_EVENT } from '@/lib/localDevMockAuth';
import { supabase } from '@/lib/supabase';
import { immoTheme } from '@/theme/theme';

// Affiliate-Referral so früh wie möglich erfassen: liest ?ref=<code> aus
// der Landing-URL, persistiert ihn (first-touch) und feuert einen anonymen
// Klick-Event. Läuft vor dem Router-Mount, damit der Code erhalten bleibt,
// auch wenn der Query-String später wegnavigiert wird.
captureRefFromUrl();

// Vite meldet fehlgeschlagene Vorab-Ladungen dynamischer Imports über dieses
// Event (z. B. Stale-Chunk nach Deploy) → einmal hart neu laden statt Fehler.
window.addEventListener('vite:preloadError', (event) => {
  if (reloadOnceForStaleChunk()) event.preventDefault();
});

/**
 * Wie React.lazy, aber fängt Stale-Chunk-Fehler ab: schlägt der dynamische
 * Import fehl, weil nach einem Deploy der alte Chunk weg ist, lädt die Seite
 * einmal hart neu (siehe src/lib/chunkReload.ts). Während des Reloads gibt der
 * Wrapper ein nie-auflösendes Promise zurück, damit kein Fehler-Flash entsteht.
 */
function lazyWithReload<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy<T>(() =>
    factory().catch((err): Promise<{ default: T }> => {
      if (isChunkLoadError(err) && reloadOnceForStaleChunk()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    })
  );
}

const AuthPage = lazyWithReload(() => import('@/pages/AuthPage').then(m => ({ default: m.AuthPage })));
const AnalysePage = lazyWithReload(() => import('@/pages/AnalysePage').then(m => ({ default: m.AnalysePage })));
const EinstellungenPage = lazyWithReload(() => import('@/pages/EinstellungenPage').then(m => ({ default: m.EinstellungenPage })));
const AdminAnalyticsPage = lazyWithReload(() => import('@/pages/AdminAnalyticsPage').then(m => ({ default: m.AdminAnalyticsPage })));
const DokuPage = lazyWithReload(() => import('@/pages/DokuPage').then(m => ({ default: m.DokuPage })));
const ImmobilienListPage = lazyWithReload(() => import('@/pages/ImmobilienListPage').then(m => ({ default: m.ImmobilienListPage })));
const ImpressumPage = lazyWithReload(() => import('@/pages/ImpressumPage').then(m => ({ default: m.ImpressumPage })));
const LandingPage = lazyWithReload(() => import('@/pages/LandingPage').then(m => ({ default: m.LandingPage })));
const PaketePage = lazyWithReload(() => import('@/pages/PaketePage').then(m => ({ default: m.PaketePage })));
const PrivacyPage = lazyWithReload(() => import('@/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })));
const TermsPage = lazyWithReload(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })));
const RenditerechnerPage = lazyWithReload(() => import('@/pages/RenditerechnerPage').then(m => ({ default: m.RenditerechnerPage })));
const VergleichPage = lazyWithReload(() => import('@/pages/VergleichPage').then(m => ({ default: m.VergleichPage })));
const KuendigungPage = lazyWithReload(() => import('@/pages/KuendigungPage').then(m => ({ default: m.KuendigungPage })));
const WiderrufPage = lazyWithReload(() => import('@/pages/WiderrufPage').then(m => ({ default: m.WiderrufPage })));

function PageLoader() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
      <CircularProgress size={32} />
    </Box>
  );
}

/**
 * Checks the URL hash for a Supabase recovery/magic-link token before
 * the Supabase client consumes and removes it. Must run synchronously
 * at module evaluation time (before any React render).
 */
function getHashParam(key: string): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  if (!hash) return null;
  return new URLSearchParams(hash).get(key);
}

const initialHashType = getHashParam('type');

/** Inner component with hooks — only mounted when NOT a recovery/magic-link flow. */
function HomeRouteInner() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    const sync = () => {
      if (!supabase) return;
      void supabase.auth.getSession().then(({ data }) => {
        setUser(data.session?.user ?? null);
        setLoading(false);
      });
    };
    sync();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUser(session?.user ?? null);
    });
    window.addEventListener(LOCAL_MOCK_AUTH_CHANGED_EVENT, sync);
    return () => {
      sub.subscription.unsubscribe();
      window.removeEventListener(LOCAL_MOCK_AUTH_CHANGED_EVENT, sync);
    };
  }, []);

  if (loading) return null;
  return user ? <ImmobilienListPage /> : <Navigate to="/landing" replace />;
}

/**
 * Renders LandingPage for guests, ImmobilienListPage for authenticated users.
 * Intercepts Supabase recovery/magic-link hashes BEFORE the Supabase client
 * consumes them, so the AuthPage can render the correct form.
 */
function HomeRoute() {
  if (initialHashType === 'recovery' || initialHashType === 'magiclink') {
    return <Navigate to={`/auth${window.location.hash}`} replace />;
  }
  return <HomeRouteInner />;
}

const root = document.getElementById('root');
if (!root) throw new Error('root missing');

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ThemeProvider theme={immoTheme}>
      <CssBaseline />
      <ErrorBoundary>
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Public pages — no AppShell wrapper */}
              <Route path="/landing" element={<LandingPage />} />
              {/* Gesetzliche Buttons — ohne Login/Consent-Hürde erreichbar */}
              <Route path="/kuendigung" element={<KuendigungPage />} />
              <Route path="/widerruf" element={<WiderrufPage />} />

              {/* App pages — inside AppShell */}
              <Route element={<AppShell />}>
                <Route path="/" element={<HomeRoute />} />
                <Route path="/analyse/:id?" element={<AnalysePage />} />
                <Route path="/einstellungen" element={<EinstellungenPage />} />
                <Route path="/admin/analytics" element={<AdminAnalyticsPage />} />
                <Route path="/doku" element={<DokuPage />} />
                <Route path="/vergleich" element={<VergleichPage />} />
                <Route path="/renditerechner" element={<RenditerechnerPage />} />
                <Route path="/pakete" element={<PaketePage />} />
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/agb" element={<TermsPage />} />
                <Route path="/datenschutz" element={<PrivacyPage />} />
                <Route path="/impressum" element={<ImpressumPage />} />
                <Route path="/terms" element={<Navigate to="/agb" replace />} />
                <Route path="/privacy" element={<Navigate to="/datenschutz" replace />} />
                {/* 404 Fallback */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Route>
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </ThemeProvider>
  </React.StrictMode>
);
